import type { EnemyState, ProjectileState, PowerUpState, GameStatus, EntityId } from '../types';
import { SCORING, DIFFICULTY } from '../config/gameConfig';

// The entire authoritative game state — plain data, JSON-serializable. This is
// what you snapshot for save/resume, send over the wire for multiplayer, or
// hand to a replay. No methods, no class instances, no Three.js.
export interface GameState {
  status: GameStatus;
  score: number;
  /** Selected answer, 0-9. */
  weapon: number;
  /** Seconds of active play elapsed (excludes paused time). */
  elapsed: number;
  level: number;
  /** Level at which the current curriculum stage began (1 outside ladders).
   *  Difficulty and label fades run band-relative to this. */
  stageFromLevel: number;
  /** Seconds until the next spawn. */
  spawnCountdown: number;
  enemies: EnemyState[];
  projectiles: ProjectileState[];
  powerups: PowerUpState[];
  /** Consecutive correct answers; drives the score multiplier (see COMBO). */
  combo: number;
  bestCombo: number;
  /** Absolute `elapsed` timestamps until which each power-up effect runs. */
  effects: { timeFreezeUntil: number; multiShotUntil: number; hintUntil: number };
  /** While the hint effect runs: the enemy the current weapon would destroy. */
  hintTargetId: EntityId | null;
  nextEntityId: number;
  seed: number;
}

export function createGameState(seed: number): GameState {
  return {
    status: 'idle',
    score: SCORING.start,
    weapon: 0,
    elapsed: 0,
    level: 1,
    stageFromLevel: 1,
    spawnCountdown: DIFFICULTY.baseSpawnMs / 1000,
    enemies: [],
    projectiles: [],
    powerups: [],
    combo: 0,
    bestCombo: 0,
    effects: { timeFreezeUntil: 0, multiShotUntil: 0, hintUntil: 0 },
    hintTargetId: null,
    nextEntityId: 1,
    seed,
  };
}

export function nextId(state: GameState): number {
  return state.nextEntityId++;
}
